import React, { useState } from "react";
import { SubmissionError } from "redux-form";
import { Id } from "storefront/lib/Id";
import buttonClicked from "storefront/Analytics/EventCreators/buttonClicked";
import {
  UserCreateError,
  createWithPassword,
} from "storefront/GrailedAPI/v1/Users/create";
import createSession, {
  Credentials,
  EmailCredentials,
  SessionCreateError,
} from "storefront/GrailedAPI/v1/Sessions/create";
import type { TwoFactorChallenge } from "storefront/GrailedAPI/NextAction";
import { Tracker } from "storefront/Analytics/Tracker";
import useAnalytics from "storefront/hooks/useAnalytics";
import { USER_EXISTS } from "./Errors";
import { AuthenticationData } from "../Data";
import SignUpForm from "./SignUpForm";
import TwoFactorChallengeForm from "../TwoFactorChallengeForm";

export type SignUpStepCompletedEvent = {
  object: "sign_up_step";
  action: "completed";
  properties: {
    emailOptIn: boolean | null | undefined;
    identityProvider: "email";
    user_id: Id;
  };
};

type Props = {
  onSuccess: (data: AuthenticationData) => void;
  onLoginClick: (e: React.SyntheticEvent<HTMLAnchorElement>) => void;
  emailSubscriptionIsChecked: boolean;
  onEmailSubscriptionChange: (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) => void;
};

type PendingTwoFactor = {
  challenge: TwoFactorChallenge;
  credentials: Credentials;
};

const createUser =
  (
    track: Tracker["track"],
    onTwoFactorRequired: (pending: PendingTwoFactor) => void,
  ) =>
  (credentials: EmailCredentials): Promise<AuthenticationData | void> => {
    track(buttonClicked("sign_up", "signup"));
    return createWithPassword(
      credentials.email,
      credentials.password,
      credentials.recaptchaToken,
      credentials.recaptchaType,
      credentials.optInEmails,
    )
      .then((user) => {
        track({
          object: "sign_up_step",
          action: "completed",
          properties: {
            emailOptIn: credentials.optInEmails,
            identityProvider: "email",
            // eslint-disable-next-line camelcase
            user_id: user.id,
          },
        });

        return {
          user,
          token: "",
          redirectTo: "",
          credentials,
        };
      })
      .catch((error: UserCreateError) => {
        switch (error.type) {
          case "IPQSFailed":
            throw new SubmissionError({
              email: error.message,
            });

          // The email belongs to an existing account, so we try signing in with the same credentials.
          case "EmailTaken": {
            return createSession(credentials).catch(
              (sessionError: SessionCreateError) => {
                if (sessionError.type === "TwoFactorRequired") {
                  onTwoFactorRequired({
                    challenge: sessionError.challenge,
                    credentials: sessionError.credentials,
                  });
                  return;
                }

                console.log(sessionError);
                throw new SubmissionError({
                  _error: USER_EXISTS,
                });
              },
            );
          }

          default:
            throw new SubmissionError({
              _error: error.message,
            });
        }
      });
  };

const SignUpEmail = ({
  onSuccess,
  onLoginClick,
  emailSubscriptionIsChecked,
  onEmailSubscriptionChange,
}: Props) => {
  const { track } = useAnalytics();
  const [pendingTwoFactor, setPendingTwoFactor] = useState<
    PendingTwoFactor | undefined
  >();

  // The form calls this with nothing when signing in to an existing account requires a two
  // factor challenge instead.
  const handleSubmitSuccess = (data: AuthenticationData | void) => {
    if (data) onSuccess(data);
  };

  const handleSignInAgain = () => setPendingTwoFactor(undefined);

  if (pendingTwoFactor) {
    return (
      <TwoFactorChallengeForm
        challenge={pendingTwoFactor.challenge}
        credentials={pendingTwoFactor.credentials}
        onSuccess={onSuccess}
        onSignInAgain={handleSignInAgain}
      />
    );
  }

  return (
    <SignUpForm
      // @ts-expect-error strictFunctionTypes - enabling strict mode
      onSubmit={createUser(track, setPendingTwoFactor)}
      submitText="Sign up"
      onLoginClick={onLoginClick}
      onSubmitSuccess={handleSubmitSuccess}
      emailSubscriptionIsChecked={emailSubscriptionIsChecked}
      onEmailSubscriptionChange={onEmailSubscriptionChange}
    />
  );
};

export default SignUpEmail;
